const CORE_LINESCORES =
    "https://sports.core.api.espn.com/v2/sports/golf/leagues/pga/events";

/** ESPN sometimes puts a tee-time UTC instant in `status.displayValue` / `todayDetail`. */
export function looksLikeIsoTimestamp(str) {
    return typeof str === "string" && /^\d{4}-\d{2}-\d{2}T/.test(str);
}

/**
 * Sum of per-round vs-par from leaderboard `linescores[].displayValue` (each row is one
 * round’s total vs par; the current round includes a running total when ESPN provides it).
 * Returns null if no round row has a score yet.
 */
export function sumLinescoresRoundsVsPar(linescores) {
    let total = 0;
    let any = false;
    for (const row of linescores || []) {
        const dv = row?.displayValue;
        if (dv == null || typeof dv !== "string") continue;
        const t = dv.trim();
        if (t === "" || t === "-" || t === "—") continue;
        const v = t === "E" || t === "e" ? 0 : parseInt(t, 10);
        if (Number.isNaN(v)) continue;
        any = true;
        total += v;
    }
    return any ? total : null;
}

/** Tournament vs par as shown on ESPN (+4, E, -2). */
export function formatTournamentVsParDisplay(n) {
    if (n == null || Number.isNaN(n)) return "—";
    if (n === 0) return "E";
    if (n > 0) return `+${n}`;
    return String(n);
}

/** Tee time from an ISO timestamp — 24h clock in UK civil time (Europe/London). */
export function formatIsoTeeTimeUK(isoOrTimestamp) {
    if (!isoOrTimestamp || typeof isoOrTimestamp !== "string") return "—";
    const d = new Date(isoOrTimestamp);
    if (Number.isNaN(d.getTime())) return "—";
    return new Intl.DateTimeFormat("en-GB", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
        timeZone: "Europe/London",
    }).format(d);
}

/**
 * Tee time from status.teeTime / displayValue — 24h clock in UK civil time
 * (Europe/London: GMT in winter, BST = UTC+1 in summer). ESPN timestamps are UTC.
 */
function formatTeeTimeGmt(status) {
    const raw = status?.teeTime ?? status?.displayValue;
    if (!raw || typeof raw !== "string") return "—";
    if (!looksLikeIsoTimestamp(raw)) return "—";
    return formatIsoTeeTimeUK(raw);
}

function holeVsParLabel(hole) {
    const raw = hole.scoreType?.displayValue;
    if (typeof raw === "string" && raw !== "" && raw !== "-") {
        return raw;
    }
    const strokes = Number(hole.displayValue);
    const par = Number(hole.par);
    if (Number.isFinite(strokes) && Number.isFinite(par)) {
        const d = strokes - par;
        if (d === 0) return "E";
        if (d > 0) return `+${d}`;
        return String(d);
    }
    return "—";
}

function linescoreItemForRound(payload, roundPeriod) {
    const p = Number(roundPeriod);
    if (!payload?.items?.length) return null;
    if (Number.isFinite(p) && p > 0) {
        const match = payload.items.find((item) => Number(item.period) === p);
        if (match) return match;
    }
    return (
        payload.items.find(
            (item) => Array.isArray(item.linescores) && item.linescores.length > 0,
        ) ?? payload.items[0]
    );
}

/**
 * Last up to 3 completed holes in the tournament round `roundPeriod` (oldest → newest),
 * each as vs par: "-1", "E", "+2", …
 */
export function parseAthleteLinescoresForm(payload, roundPeriod = 1) {
    const roundItem = linescoreItemForRound(payload, roundPeriod);
    const holes = roundItem?.linescores;
    if (!Array.isArray(holes) || holes.length === 0) return null;
    const lastThree = holes.slice(-3);
    return {
        lastThreeVsPar: lastThree.map((h) => holeVsParLabel(h)),
    };
}

/**
 * All 18 holes for `roundPeriod` (1–4). `vsParLabel` null if not yet played.
 */
export function parseAthleteCurrentRoundHoles(payload, roundPeriod) {
    const slots = Array.from({ length: 18 }, (_, i) => ({
        hole: i + 1,
        vsParLabel: null,
    }));
    const roundItem = linescoreItemForRound(payload, roundPeriod);
    const holes = roundItem?.linescores;
    if (!Array.isArray(holes)) return slots;
    for (const h of holes) {
        const hn = Number(h.period);
        if (Number.isFinite(hn) && hn >= 1 && hn <= 18) {
            slots[hn - 1].vsParLabel = holeVsParLabel(h);
        }
    }
    return slots;
}

function vsParNumberFromDisplayValue(dv) {
    if (dv == null || dv === "") return null;
    const s = String(dv).trim();
    if (s === "E" || s === "-") return 0;
    const n = parseInt(s, 10);
    return Number.isNaN(n) ? null : n;
}

function vsParNumberFromHoleLabel(lbl) {
    if (lbl == null || lbl === "—") return null;
    if (lbl === "E") return 0;
    if (lbl.startsWith("+")) return parseInt(lbl.slice(1), 10);
    return parseInt(lbl, 10);
}

/**
 * Tournament total vs par from core linescores payload (full field; leaderboard JSON is often sparse).
 * Prefers per-round `displayValue` when present; otherwise sums hole vs-par for each round.
 */
export function tournamentVsParFromLinescoresPayload(payload) {
    const items = payload?.items;
    if (!Array.isArray(items) || items.length === 0) return null;
    let total = 0;
    let any = false;
    for (const item of items) {
        const dv = item?.displayValue;
        if (dv != null && String(dv).trim() !== "") {
            const v = vsParNumberFromDisplayValue(dv);
            if (v !== null) {
                total += v;
                any = true;
            }
            continue;
        }
        const holeList = item?.linescores;
        if (!Array.isArray(holeList)) continue;
        for (const h of holeList) {
            const n = vsParNumberFromHoleLabel(holeVsParLabel(h));
            if (n === null) continue;
            any = true;
            total += n;
        }
    }
    return any ? total : null;
}

/** Current tournament round vs par from core linescores payload. */
export function currentRoundVsParFromLinescoresPayload(payload, roundPeriod) {
    const roundItem = linescoreItemForRound(payload, roundPeriod);
    if (!roundItem) return null;
    const dv = roundItem.displayValue;
    if (dv != null && String(dv).trim() !== "") {
        return vsParNumberFromDisplayValue(dv);
    }
    const holeList = roundItem.linescores;
    if (!Array.isArray(holeList)) return null;
    let total = 0;
    let any = false;
    for (const h of holeList) {
        const n = vsParNumberFromHoleLabel(holeVsParLabel(h));
        if (n === null) continue;
        any = true;
        total += n;
    }
    return any ? total : null;
}

/** Par for one regulation round at Augusta National (used for gross vs 72 UI). */
export const MASTERS_ROUND_PAR = 72;

/**
 * When a player has completed the current round (thru 18), ESPN includes a
 * numeric `value` on the matching `linescores` row — gross strokes for that round.
 */
export function completedRoundGrossStrokes(status, linescores) {
    const thruRaw = status?.thru;
    const thruNum =
        thruRaw != null && thruRaw !== "" ? Number(thruRaw) : Number.NaN;
    if (!Number.isFinite(thruNum) || thruNum !== 18) return null;

    const period = Number(status?.period);
    if (!Number.isFinite(period)) return null;

    const line = (linescores || []).find(
        (ls) =>
            Number(ls.period) === period &&
            typeof ls.value === "number" &&
            !Number.isNaN(ls.value),
    );
    if (!line) return null;
    return Math.round(line.value);
}

export function buildRoundDisplayLines(status) {
    const typeName = status?.type?.name;
    if (typeName === "STATUS_SCHEDULED") {
        return {
            roundThruLabel: formatTeeTimeGmt(status),
        };
    }

    const thruRaw = status?.thru;
    const thruNum =
        thruRaw != null && thruRaw !== "" ? Number(thruRaw) : Number.NaN;
    const hasThru = Number.isFinite(thruNum) && thruNum > 0;

    let roundThruLabel = null;
    if (hasThru) {
        roundThruLabel = `Thru ${thruNum}`;
    } else if (looksLikeIsoTimestamp(status?.displayValue)) {
        roundThruLabel = formatTeeTimeGmt(status);
    } else if (status?.displayValue) {
        roundThruLabel = status.displayValue;
    }

    return { roundThruLabel };
}

export async function fetchAthleteLinescoresPayload(eventId, athleteId) {
    const url = `${CORE_LINESCORES}/${eventId}/competitions/${eventId}/competitors/${athleteId}/linescores?lang=en&region=us`;
    const response = await fetch(url);
    if (!response.ok) return null;
    return response.json();
}

export async function fetchAthleteLinescoresForm(
    eventId,
    athleteId,
    roundPeriod = 1,
) {
    const payload = await fetchAthleteLinescoresPayload(eventId, athleteId);
    if (!payload) return null;
    return parseAthleteLinescoresForm(payload, roundPeriod);
}

export async function fetchAthleteCurrentRoundHoles(
    eventId,
    athleteId,
    roundPeriod,
) {
    const payload = await fetchAthleteLinescoresPayload(eventId, athleteId);
    if (!payload) return null;
    return parseAthleteCurrentRoundHoles(payload, roundPeriod);
}

/** Regulation rounds shown in the player modal (Masters / four-round event). */
const REGULATION_ROUNDS_SHOWN = 4;

/**
 * Gross strokes for a **completed** round (18 holes with stroke counts), or null.
 * Prefers ESPN `value` on the round row when present.
 */
function completedGrossStrokesForRoundItem(roundItem) {
    if (!roundItem) return null;
    if (
        typeof roundItem.value === "number" &&
        !Number.isNaN(roundItem.value)
    ) {
        return Math.round(roundItem.value);
    }
    const holes = roundItem.linescores;
    if (!Array.isArray(holes)) return null;
    const byHole = new Map();
    for (const h of holes) {
        const hn = Number(h.period);
        if (!Number.isFinite(hn) || hn < 1 || hn > 18) continue;
        const s = Number(h.displayValue);
        if (!Number.isFinite(s)) continue;
        byHole.set(hn, s);
    }
    if (byHole.size !== 18) return null;
    let sum = 0;
    for (let i = 1; i <= 18; i += 1) {
        sum += byHole.get(i);
    }
    return sum;
}

const EMPTY_ROUND_GROSS_LABEL = "-";

/**
 * Four gross round totals for the modal (e.g. `["67","71","-","-"]`).
 * Only shows a number once that round is complete (18 hole scores or `value`).
 * Rounds after the event’s `competitionPeriod` stay as placeholders.
 */
export function fourRoundGrossLabelsFromPayload(payload, competitionPeriod) {
    const cp = Number(competitionPeriod);
    const labels = [];
    for (let r = 1; r <= REGULATION_ROUNDS_SHOWN; r += 1) {
        if (!Number.isFinite(cp) || r > cp) {
            labels.push(EMPTY_ROUND_GROSS_LABEL);
            continue;
        }
        const item = linescoreItemForRound(payload, r);
        const gross = completedGrossStrokesForRoundItem(item);
        labels.push(
            gross != null ? String(gross) : EMPTY_ROUND_GROSS_LABEL,
        );
    }
    return labels;
}

/**
 * Full linescores payload + parsed holes + vs-par totals (one network round-trip).
 */
export async function fetchAthleteLinescoresDetail(
    eventId,
    athleteId,
    roundPeriod,
) {
    const payload = await fetchAthleteLinescoresPayload(eventId, athleteId);
    if (!payload) return null;
    return {
        holes: parseAthleteCurrentRoundHoles(payload, roundPeriod),
        tournamentVsPar: tournamentVsParFromLinescoresPayload(payload),
        currentRoundVsPar: currentRoundVsParFromLinescoresPayload(
            payload,
            roundPeriod,
        ),
        roundGrossLabels: fourRoundGrossLabelsFromPayload(
            payload,
            roundPeriod,
        ),
    };
}

/**
 * Parallel fetch of recent-hole vs-par form for many athletes (picked players only).
 */
export async function fetchLastHolesForAthleteIds(
    eventId,
    athleteIds,
    roundPeriod = 1,
) {
    const unique = [...new Set(athleteIds.map(String))];
    const entries = await Promise.all(
        unique.map(async (id) => {
            try {
                const form = await fetchAthleteLinescoresForm(
                    eventId,
                    id,
                    roundPeriod,
                );
                return [id, form];
            } catch {
                return [id, null];
            }
        }),
    );
    return Object.fromEntries(entries);
}
